<script setup>
import { computed, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import ButtonNext from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import Logo from 'next/icon/Logo.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';

defineProps({
  isCollapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['showCreateAccountModal']);

const { t } = useI18n();
const { accountId, currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');

const userAccounts = useMapGetter('getUserAccounts');
const accountSearch = ref('');

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value.name
);

const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) => {
    if (a.id === accountId.value) return -1;
    if (b.id === accountId.value) return 1;
    return a.name.localeCompare(b.name);
  });
});

const normalizeSearch = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const filteredAccounts = computed(() => {
  const query = normalizeSearch(accountSearch.value);
  if (!query) return sortedCurrentUserAccounts.value;

  return sortedCurrentUserAccounts.value.filter(account => {
    const role = account.custom_role_id
      ? account.custom_role?.name
      : account.role;
    return normalizeSearch(`${account.name} ${role}`).includes(query);
  });
});

const accountInitials = name => {
  const words = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '—';
  return `${words[0][0]}${words.length > 1 ? words.at(-1)[0] : ''}`.toUpperCase();
};

const accountRole = account =>
  account.custom_role_id ? account.custom_role?.name : account.role;

const onChangeAccount = newId => {
  const accountUrl = `/app/accounts/${newId}/dashboard`;
  window.location.href = accountUrl;
};

const emitNewAccount = () => {
  emit('showCreateAccountModal');
};
</script>

<template>
  <DropdownContainer @close="accountSearch = ''">
    <template #trigger="{ toggle, isOpen }">
      <!-- Collapsed view: Logo trigger -->
      <button
        v-if="isCollapsed"
        v-tooltip.right="{
          content: currentAccount.name,
          delay: { show: 300, hide: 0 },
        }"
        class="grid flex-shrink-0 place-content-center p-2 rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="{ 'bg-n-alpha-1': isOpen }"
        :aria-label="currentAccount.name"
        @click="toggle"
      >
        <Logo class="size-7" />
      </button>
      <!-- Expanded view: Account name trigger -->
      <button
        v-else
        id="sidebar-account-switcher"
        :data-account-id="accountId"
        aria-haspopup="listbox"
        aria-controls="account-options"
        class="flex h-10 w-full items-center justify-between gap-2 rounded-xl border border-n-weak bg-n-solid-2 px-2.5 shadow-sm transition"
        :class="[
          isOpen && 'bg-n-alpha-1',
          showAccountSwitcher
            ? 'hover:bg-n-alpha-1 cursor-pointer'
            : 'cursor-default',
        ]"
        @click="() => showAccountSwitcher && toggle()"
      >
        <span class="flex min-w-0 items-center gap-2.5">
          <span
            class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-n-brand/10 text-[10px] font-bold tracking-wide text-n-brand"
          >
            {{ accountInitials(currentAccount.name) }}
          </span>
          <span
            class="truncate text-sm font-semibold leading-5 text-n-slate-12"
            aria-live="polite"
          >
            {{ currentAccount.name }}
          </span>
        </span>

        <span
          v-if="showAccountSwitcher"
          aria-hidden="true"
          class="i-lucide-chevron-down size-4 flex-shrink-0 text-n-slate-10 transition-transform duration-200"
          :class="isOpen && 'rotate-180'"
        />
      </button>
    </template>
    <DropdownBody
      v-if="showAccountSwitcher || isCollapsed"
      class="z-50 !w-[min(22rem,calc(100vw-1.5rem))] !overflow-hidden [&>ul]:!gap-0 [&>ul]:!rounded-2xl [&>ul]:!border-n-weak [&>ul]:!bg-n-solid-2 [&>ul]:!p-0 [&>ul]:!shadow-xl [&>ul]:!backdrop-blur-none"
    >
      <li class="border-b border-n-weak px-3 pb-2 pt-3">
        <div class="mb-2.5 flex items-start justify-between gap-3">
          <div>
            <p class="m-0 text-sm font-semibold text-n-slate-12">
              {{ t('SIDEBAR_ITEMS.SWITCH_ACCOUNT') }}
            </p>
            <p class="m-0 mt-0.5 text-xs leading-5 text-n-slate-10">
              {{ t('SIDEBAR_ITEMS.SELECTOR_SUBTITLE') }}
            </p>
          </div>
          <span
            class="shrink-0 rounded-full bg-n-alpha-2 px-2 py-1 text-[11px] font-semibold text-n-slate-11"
          >
            {{ sortedCurrentUserAccounts.length }}
          </span>
        </div>
        <label class="relative block">
          <span
            class="i-lucide-search pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-9 ltr:left-4 rtl:right-4"
          />
          <input
            v-model="accountSearch"
            type="search"
            class="h-10 w-full appearance-none rounded-xl border border-n-weak bg-n-alpha-1 !pe-3 !ps-11 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-9 hover:border-n-strong focus:border-n-brand focus:ring-1 focus:ring-n-brand"
            :placeholder="t('DROPDOWN_MENU.SEARCH_PLACEHOLDER')"
            :aria-label="t('DROPDOWN_MENU.SEARCH_PLACEHOLDER')"
          />
        </label>
      </li>

      <DropdownSection
        class="!mx-0 !pb-1.5 !pt-1.5 [&>ul]:!gap-1 [&>ul]:!px-1.5"
        height="max-h-80"
      >
        <DropdownItem
          v-for="account in filteredAccounts"
          :id="`account-${account.id}`"
          :key="account.id"
          role="option"
          class="group cursor-pointer !rounded-xl !p-2.5 transition"
          :class="
            account.id === accountId
              ? 'bg-n-brand/10 ring-1 ring-inset ring-n-brand/20'
              : 'hover:bg-n-alpha-2'
          "
          :aria-selected="account.id === accountId"
          @click="onChangeAccount(account.id)"
        >
          <template #label>
            <div
              class="flex w-full min-w-0 items-center gap-3 text-left rtl:text-right"
            >
              <span
                class="flex size-9 shrink-0 items-center justify-center rounded-xl border border-n-weak bg-n-alpha-2 text-xs font-bold tracking-wide text-n-slate-11 group-hover:border-n-strong"
                :class="
                  account.id === accountId &&
                  'border-n-brand/20 bg-n-brand/10 text-n-brand'
                "
              >
                {{ accountInitials(account.name) }}
              </span>
              <span class="min-w-0 flex-1">
                <span
                  class="block truncate text-sm font-medium text-n-slate-12"
                  :title="account.name"
                >
                  {{ account.name }}
                </span>
                <span
                  class="mt-0.5 block truncate text-xs capitalize text-n-slate-10"
                >
                  {{ accountRole(account) }}
                </span>
              </span>
            </div>
            <Icon
              v-show="account.id === accountId"
              icon="i-lucide-check"
              class="size-5 shrink-0 text-n-brand"
            />
          </template>
        </DropdownItem>
        <li
          v-if="!filteredAccounts.length"
          class="flex flex-col items-center px-4 py-8 text-center"
        >
          <span class="i-lucide-building-2 mb-3 size-6 text-n-slate-9" />
          <p class="m-0 text-sm font-medium text-n-slate-12">
            {{ t('DROPDOWN_MENU.EMPTY_STATE') }}
          </p>
        </li>
      </DropdownSection>
      <DropdownItem
        v-if="globalConfig.createNewAccountFromDashboard"
        class="border-t border-n-weak !p-3"
      >
        <ButtonNext
          color="slate"
          variant="faded"
          class="w-full"
          size="sm"
          @click="emitNewAccount"
        >
          {{ t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
        </ButtonNext>
      </DropdownItem>
    </DropdownBody>
  </DropdownContainer>
</template>
